import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ACCESS_TOKEN_EXPIRY_LEEWAY_MS,
  getAccessTokenExpiry,
  getAccessTokenSub,
  isAccessTokenExpired,
} from '@/lib/access-token';

const ACCESS_TOKEN_STORAGE_KEY = 'portfolio-manager.access-token';

const USER_ID_STORAGE_KEY = 'portfolio-manager.user-id';

const MAX_TIMEOUT_MS = 2_147_483_647;

type AuthStorageCredentials = { accessToken: string; userId: string };

const storage = {
  read(): AuthStorageCredentials | null {
    try {
      const accessToken = window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
      const userId = window.localStorage.getItem(USER_ID_STORAGE_KEY);
      return accessToken && userId ? { accessToken, userId } : null;
    } catch {
      return null;
    }
  },
  write(credentials: AuthStorageCredentials): void {
    try {
      window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, credentials.accessToken);
      window.localStorage.setItem(USER_ID_STORAGE_KEY, credentials.userId);
    } catch {
      return;
    }
  },
  clear(): void {
    try {
      window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      window.localStorage.removeItem(USER_ID_STORAGE_KEY);
    } catch {
      return;
    }
  },
};

function readUnexpiredCredentials(): AuthStorageCredentials | null {
  const value = storage.read();
  if (value === null) {
    return null;
  }
  if (isAccessTokenExpired(value.accessToken)) {
    storage.clear();
    return null;
  }
  return value;
}

type AuthServiceState =
  | {
      isAuthenticated: true;
      getAccessToken: () => string;
      getUserId: () => string;
    }
  | {
      isAuthenticated: false;
      getAccessToken: () => null;
      getUserId: () => null;
    };

export type AuthService = AuthServiceState & {
  signIn: (accessToken: string) => void;
  signOut: () => void;
};

const AuthServiceContext = createContext<AuthService | null>(null);

export function AuthServiceProvider({ children }: React.PropsWithChildren) {
  const [credentials, setCredentials] = useState<AuthStorageCredentials | null>(readUnexpiredCredentials);

  const signIn = useCallback((accessToken: string) => {
    const sub = getAccessTokenSub(accessToken);
    if (sub) {
      const credentials: AuthStorageCredentials = { accessToken, userId: sub };
      storage.write(credentials);
      setCredentials(credentials);
    }
  }, []);

  const signOut = useCallback(() => {
    storage.clear();
    setCredentials(null);
  }, []);

  useEffect(() => {
    if (credentials === null) {
      return;
    }
    const expiresAt = getAccessTokenExpiry(credentials.accessToken);
    if (expiresAt === null) {
      signOut();
      return;
    }
    let timeout: number | undefined;
    const scheduleExpiry = () => {
      const delay = expiresAt - ACCESS_TOKEN_EXPIRY_LEEWAY_MS - Date.now();
      if (delay <= 0) {
        signOut();
        return;
      }
      timeout = window.setTimeout(scheduleExpiry, Math.min(delay, MAX_TIMEOUT_MS));
    };
    const signOutWhenExpired = () => {
      if (isAccessTokenExpired(credentials.accessToken)) {
        signOut();
      }
    };
    scheduleExpiry();
    window.addEventListener('focus', signOutWhenExpired);
    document.addEventListener('visibilitychange', signOutWhenExpired);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('focus', signOutWhenExpired);
      document.removeEventListener('visibilitychange', signOutWhenExpired);
    };
  }, [credentials, signOut]);

  const value: AuthService = useMemo(
    () =>
      credentials === null
        ? { isAuthenticated: false, getAccessToken: () => null, getUserId: () => null, signIn, signOut }
        : {
            isAuthenticated: true,
            getAccessToken: () => credentials.accessToken,
            getUserId: () => credentials.userId,
            signIn,
            signOut,
          },
    [credentials, signIn, signOut],
  );

  return <AuthServiceContext.Provider value={value}>{children}</AuthServiceContext.Provider>;
}

export function useAuthService(): AuthService {
  const context = useContext(AuthServiceContext);

  if (context === null) {
    throw new Error('useAuthService must be used within a <AuthServiceProvider>.');
  }

  return context;
}
